import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Rocket, CheckCircle, Lightning, ArrowRight, Play, Pause,
  Calendar, Clock, VideoCamera, Image as ImageIcon, ListBullets,
  Package, Sparkle, Download, CaretDown, CaretUp, SpinnerGap,
  Target, Users, Money, MagicWand, Eye, Warning, ArrowsClockwise,
  PaperPlaneTilt, Link, CheckSquare
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '@/utils/api';
import BusinessMemoryBanner from './BusinessMemoryBanner';
import { downloadBlob as safeDownloadBlob } from '@/utils/downloadBlob';
import TaskExecutionMeta from './TaskExecutionMeta';

// Progress step configuration
const PROGRESS_STEPS = [
  { key: 'business_plan', icon: Target, label: 'Business Plan', description: 'Your strategy is set' },
  { key: 'offer', icon: Money, label: 'Offer Ready', description: 'Foundation assets created' },
  { key: 'content', icon: VideoCamera, label: 'Content Created', description: '14 Reels + 21 Stories' },
  { key: 'schedule', icon: Calendar, label: 'Schedule Ready', description: 'Metricool-ready' }
];

// Day theme descriptions
const DAY_THEMES = [
  { day: 1, theme: 'Introduction & Problem Awareness', color: 'from-blue-500 to-purple-500' },
  { day: 2, theme: 'Authority & Value', color: 'from-purple-500 to-pink-500' },
  { day: 3, theme: 'Social Proof & Results', color: 'from-pink-500 to-rose-500' },
  { day: 4, theme: 'Objection Handling', color: 'from-rose-500 to-orange-500' },
  { day: 5, theme: 'Education & Value', color: 'from-orange-500 to-amber-500' },
  { day: 6, theme: 'Connection & Lifestyle', color: 'from-amber-500 to-green-500' },
  { day: 7, theme: 'Call to Action & Momentum', color: 'from-green-500 to-emerald-500' }
];

// Progress Step Component
const ProgressStep = ({ step, status, isActive, index, totalSteps }) => {
  const Icon = step.icon;
  const isComplete = status === 'complete';
  const isPending = status === 'pending';
  const isInProgress = status === 'in_progress';
  
  return (
    <div className="flex items-center">
      <div className="flex flex-col items-center">
        <motion.div
          initial={{ scale: 0.8 }}
          animate={{ scale: isActive ? 1.1 : 1 }}
          className={`
            w-12 h-12 rounded-full flex items-center justify-center
            ${isComplete ? 'bg-green-500' : isInProgress ? 'bg-purple-500 animate-pulse' : 'bg-slate-700'}
            ${isActive ? 'ring-4 ring-purple-500/30' : ''}
            transition-all duration-300
          `}
        >
          {isComplete ? (
            <CheckCircle size={24} className="text-white" weight="fill" />
          ) : isInProgress ? (
            <SpinnerGap size={24} className="text-white animate-spin" />
          ) : (
            <Icon size={24} className="text-slate-400" />
          )}
        </motion.div>
        <p className={`mt-2 text-xs font-medium text-center ${isComplete ? 'text-green-400' : isInProgress ? 'text-purple-400' : 'text-slate-500'}`}>
          {step.label}
        </p>
      </div>
      
      {/* Connector line */}
      {index < totalSteps - 1 && (
        <div className={`w-8 h-0.5 mx-2 ${isComplete ? 'bg-green-500' : 'bg-slate-700'}`} />
      )}
    </div>
  );
};

// Day Card Component
const DayCard = ({ day, theme, color, posts, expanded, onToggle, isToday }) => {
  // Local stringifier — same intent as toText in the parent: never
  // print raw objects, which used to dump JSON into the schedule
  // cards when the backend returned an object where a string was
  // expected (e.g. cta: {label, link}).
  const safe = (v, max = 280) => {
    if (v === null || v === undefined) return '';
    if (typeof v === 'string') return v.slice(0, max);
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    if (Array.isArray(v)) return v.map((x) => safe(x, 60)).filter(Boolean).join(', ').slice(0, max);
    if (typeof v === 'object') {
      return (v.title || v.name || v.text || v.label || v.cta || '').toString().slice(0, max);
    }
    return String(v).slice(0, max);
  };
  const reels = posts?.filter(p => p.content_type === 'reel') || [];
  const stories = posts?.filter(p => p.content_type === 'story') || [];

  // 2026-05-29: Single-post import to Weekly Planner / Metricool Scheduler.
  // NEVER triggers video generation — see /api/weekly-planner/items/import-from-rmb.
  const handleSendToPlanner = async (post) => {
    if (!post?.id) {
      toast.error('This post has no id — please regenerate the schedule.');
      return;
    }
    try {
      const { weeklyPlannerAPI } = await import('@/utils/api');
      const res = await weeklyPlannerAPI.importFromRMB([post.id]);
      if (res.imported_count >= 1) {
        toast.success('Sent to Weekly Planner');
      } else if (res.duplicate_count >= 1) {
        toast.error('Already in Weekly Planner — edit the existing draft.');
      } else {
        toast.error('Could not import (id not found).');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || 'Send to Planner failed');
    }
  };

  return (
    <div 
      className={`bg-slate-800/50 border ${isToday ? 'border-purple-500' : 'border-slate-700/50'} rounded-xl overflow-hidden`}
      data-testid={`day-card-${day}`}
    >
      <div 
        className="p-4 cursor-pointer hover:bg-slate-800/70 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center`}>
              <span className="text-white font-bold">{day}</span>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="text-white font-medium">Day {day}</h4>
                {isToday && (
                  <span className="px-2 py-0.5 bg-purple-500/20 text-purple-300 text-xs rounded-full">Today</span>
                )}
              </div>
              <p className="text-slate-400 text-sm">{theme}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm">
              <span className="px-2 py-1 bg-purple-500/20 text-purple-300 rounded">{reels.length} Reels</span>
              <span className="px-2 py-1 bg-pink-500/20 text-pink-300 rounded">{stories.length} Stories</span>
            </div>
            <motion.div animate={{ rotate: expanded ? 180 : 0 }}>
              <CaretDown size={18} className="text-slate-400" />
            </motion.div>
          </div>
        </div>
      </div>
      
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-slate-700/50"
          >
            <div className="p-4 space-y-3">
              {/* Reels */}
              {reels.map((reel, idx) => (
                <div key={idx} className="p-3 bg-slate-900/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <VideoCamera size={16} className="text-purple-400" />
                    <span className="text-white text-sm font-medium">Reel {idx + 1}</span>
                    <span className="text-slate-500 text-xs">{safe(reel.scheduled_time, 40)}</span>
                  </div>
                  <p className="text-white text-sm font-medium">{safe(reel.title, 140)}</p>
                  <p className="text-slate-400 text-xs mt-1">{safe(reel.hook, 240)}</p>
                  <p className="text-purple-400 text-xs mt-2">{safe(reel.cta, 140)}</p>
                  <button
                    onClick={() => handleSendToPlanner(reel)}
                    className="mt-2 px-2 py-1 text-xs rounded border border-slate-600 text-slate-200 hover:border-emerald-400 hover:text-emerald-300"
                    data-testid={`rmb-send-to-planner-reel-${day}-${idx}`}
                  >
                    Send to Weekly Planner
                  </button>
                </div>
              ))}
              
              {/* Stories */}
              {stories.map((story, idx) => (
                <div key={idx} className="p-3 bg-slate-900/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <ImageIcon size={16} className="text-pink-400" />
                    <span className="text-white text-sm font-medium">Story {idx + 1}</span>
                    <span className="text-slate-500 text-xs">{safe(story.scheduled_time, 40)}</span>
                  </div>
                  <p className="text-white text-sm">{safe(story.title, 140)}</p>
                  <p className="text-slate-400 text-xs mt-1">{safe(story.caption, 240)}</p>
                  <button
                    onClick={() => handleSendToPlanner(story)}
                    className="mt-2 px-2 py-1 text-xs rounded border border-slate-600 text-slate-200 hover:border-emerald-400 hover:text-emerald-300"
                    data-testid={`rmb-send-to-planner-story-${day}-${idx}`}
                  >
                    Send to Weekly Planner
                  </button>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const RunMyBusinessModal = ({ isOpen, onClose, user, onSuccess }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [expandedDays, setExpandedDays] = useState({});
  const [activeTab, setActiveTab] = useState('overview'); // overview, schedule, actions
  const [metricoolStatus, setMetricoolStatus] = useState(null);
  const [pushingToMetricool, setPushingToMetricool] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  // Round 34 — tier-specific execution: each day's actions carry
  // `where_to_use` + `tier_asset` from the backend so users see
  // platform guidance + tier-correct copy.
  const [dailyPlans, setDailyPlans] = useState({});
  // Phase 2 — asset-driven summary from GET /api/run-my-business-v2/summary.
  // Used to surface real asset counts, today's mission, next actions
  // and drafts above the legacy Week-1 status. Layout unchanged;
  // module is hidden when v2 returns nothing (legacy fallback).
  const [v2Summary, setV2Summary] = useState(null);

  // Safe stringifier — prevents React from blowing up when a meta
  // field is unexpectedly an object/array (root cause of the
  // "raw JSON rendering" bug reported in production).
  const toText = (v, maxLen = 280) => {
    if (v === null || v === undefined) return '';
    if (typeof v === 'string') return v.slice(0, maxLen);
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    if (Array.isArray(v)) {
      return v.map((x) => toText(x, 60)).filter(Boolean).join(', ').slice(0, maxLen);
    }
    if (typeof v === 'object') {
      return (v.title || v.name || v.headline || v.label || v.text || '').toString().slice(0, maxLen);
    }
    return String(v).slice(0, maxLen);
  };
  
  const planTier = user?.plan_tier || 'starter';

  const fetchMetricoolStatus = async () => {
    try {
      const response = await api.get('/metricool/status');
      setMetricoolStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch Metricool status:', error);
    }
  };

  const fetchStatus = useCallback(async () => {
    try {
      const response = await api.get('/run-my-business/week1/status');
      setStatus(response.data);
      
      // If complete, fetch schedule and Metricool status
      if (response.data.status === 'complete') {
        fetchSchedule();
        fetchMetricoolStatus();
        // Round 218D P0 (2026-02-07) — resolver-driven Continue.
        try { onSuccess?.({ meta: { title: 'Run My Business complete.' } }); } catch (_) { /* noop */ }
        
        // Check push status
        try {
          const pushStatusRes = await api.get('/metricool/week1/push-status');
          if (pushStatusRes.data.pushed) {
            setPushResult(pushStatusRes.data.push_result);
          }
        } catch (e) {}
      }
    } catch (error) {
      console.error('Failed to fetch status:', error);
      toast.error('Failed to load status');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSchedule = async () => {
    try {
      const response = await api.get('/run-my-business/week1/schedule');
      if (response.data.success) {
        setSchedule(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch schedule:', error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchStatus();
      // Phase 2 — pull the asset-driven summary alongside the legacy
      // Week-1 status. Non-fatal if v2 endpoint is missing or empty.
      (async () => {
        try {
          const res = await api.get('/run-my-business-v2/summary');
          setV2Summary(res?.data || null);
        } catch (err) {
          console.warn('run-my-business-v2 unavailable:', err);
          setV2Summary(null);
        }
      })();
    }
  }, [isOpen, fetchStatus]);

  // Round 34 — fetch tier-specific daily action plans when the
  // actions tab opens. Cached per day so re-opening is instant.
  useEffect(() => {
    if (!isOpen || activeTab !== 'actions' || status?.status !== 'complete') return;
    let cancelled = false;
    (async () => {
      const next = { ...dailyPlans };
      for (let day = 1; day <= 7; day++) {
        if (next[day]) continue;
        try {
          const res = await api.get(`/run-my-business/week1/daily-plan/${day}`);
          if (cancelled) return;
          if (res.data?.success) {
            next[day] = res.data;
          }
        } catch (e) {
          // Soft-fail — fall back to static checklist for this day.
        }
      }
      if (!cancelled) setDailyPlans(next);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeTab, status?.status]);

  const handleGenerateAll = async () => {
    setGenerating(true);
    try {
      const response = await api.post('/run-my-business/week1/generate-all');
      
      if (response.data.success) {
        toast.success(response.data.message || 'Your first week is fully prepared!');
        fetchStatus();
      } else {
        toast.error(response.data.error || 'Generation failed');
      }
    } catch (error) {
      console.error('Generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate content');
    } finally {
      setGenerating(false);
    }
  };

  const handleExportSchedule = async () => {
    /* Phase G — V2 grants export to every subscriber */
    try {
      const response = await api.get('/run-my-business/week1/export-schedule');
      
      if (response.data.success) {
        // Download as JSON file (iOS-Safari-safe)
        const blob = new Blob([JSON.stringify(response.data.schedule, null, 2)], { type: 'application/json' });
        const res = await safeDownloadBlob(blob, 'metricool_schedule.json');
        if (res.ios) {
          toast.success("Schedule opened — tap Share → Save to Files.", { duration: 6000 });
        } else {
          toast.success('Schedule exported successfully!');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Export requires Growth or Pro plan');
    }
  };

  const handleReset = async () => {
    if (!confirm('Are you sure you want to reset? This will delete all generated content.')) return;
    
    try {
      await api.post('/run-my-business/week1/reset');
      toast.success('Week 1 reset. You can start fresh.');
      fetchStatus();
      setSchedule(null);
      setPushResult(null);
    } catch (error) {
      toast.error('Failed to reset');
    }
  };

  const handlePushToMetricool = async () => {
    if (!metricoolStatus?.connected) {
      toast.error('Please connect your Metricool account in Settings first');
      return;
    }

    setPushingToMetricool(true);
    try {
      const response = await api.post('/metricool/push/week1');
      
      if (response.data.success) {
        setPushResult(response.data);
        toast.success(response.data.message || `${response.data.sent} posts sent to Metricool!`);
        
        // Refresh Metricool status
        fetchMetricoolStatus();
      } else {
        toast.error(response.data.error || 'Failed to push to Metricool');
      }
    } catch (error) {
      console.error('Push to Metricool error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to push to Metricool';
      toast.error(errorMsg);
      
      // Show fallback option
      if (error.response?.status === 403) {
        toast.info('You can still export the schedule as JSON', { duration: 5000 });
      }
    } finally {
      setPushingToMetricool(false);
    }
  };

  const toggleDay = (day) => {
    setExpandedDays(prev => ({ ...prev, [day]: !prev[day] }));
  };

  if (!isOpen) return null;

  const isComplete = status?.status === 'complete';
  const canStart = status?.can_start && status?.status === 'not_started';
  const isInProgress = status?.status === 'in_progress';

  // Group posts by day
  const postsByDay = {};
  if (schedule?.posts) {
    schedule.posts.forEach(post => {
      if (!postsByDay[post.day]) postsByDay[post.day] = [];
      postsByDay[post.day].push(post);
    });
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
          onClick={e => e.stopPropagation()}
          data-testid="run-my-business-modal"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                  <Rocket size={24} className="text-white" weight="fill" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Run My Business — Week 1</h2>
                  <p className="text-white/80 text-sm">Your automated business launch system</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                data-testid="close-run-modal"
              >
                <X size={24} className="text-white" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
            {/* Phase 2 — Asset-driven summary. Renders only when v2
                returns at least one tracked asset, so accounts with
                no registry data fall back cleanly to the legacy
                Week-1 panels below. Uses existing color tokens; no
                new components. */}
            {v2Summary?.totals?.total > 0 && (
              <div
                className="mb-5 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4"
                data-testid="rmb-v2-summary"
              >
                {/* Active business pill — confirms which profile
                    these assets belong to (root-cause fix for
                    cross-business asset leakage). */}
                {v2Summary.active_business?.business_name && (
                  <div className="mb-2 flex items-center gap-2" data-testid="rmb-v2-active-business">
                    <span className="text-[10px] uppercase tracking-wider text-emerald-300/80">Business</span>
                    <span className="text-white text-xs font-semibold truncate max-w-[260px]">
                      {toText(v2Summary.active_business.business_name, 60)}
                    </span>
                    {v2Summary.active_business.niche && (
                      <span className="text-slate-300 text-xs truncate max-w-[200px]">
                        · {toText(v2Summary.active_business.niche, 40)}
                      </span>
                    )}
                  </div>
                )}
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-emerald-300 font-semibold">
                      Your assets
                    </p>
                    <p className="text-white text-base font-semibold">
                      {v2Summary.totals.total} saved · {(v2Summary.totals.by_status?.ready || 0)} ready to publish
                    </p>
                  </div>
                  {v2Summary.todays_mission?.action && (
                    <div className="text-right">
                      <p className="text-xs text-emerald-300/80 uppercase tracking-wider">Today</p>
                      <p
                        className="text-white text-sm font-medium truncate max-w-[420px]"
                        data-testid="rmb-v2-todays-mission"
                      >
                        {toText(v2Summary.todays_mission.action, 200)}
                      </p>
                    </div>
                  )}
                </div>
                {(v2Summary.next_actions || []).length > 0 && (
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {v2Summary.next_actions.slice(0, 4).map((a) => (
                      <div
                        key={a.asset_id}
                        className="flex items-start gap-2 rounded-lg bg-white/5 px-3 py-2"
                        data-testid={`rmb-v2-next-${a.asset_type}`}
                      >
                        <CheckSquare size={16} className="text-emerald-300 shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <p className="text-[10px] uppercase tracking-wider text-slate-400">
                            {(a.asset_type || '').replace(/_/g, ' ')}
                          </p>
                          <p className="text-white text-sm truncate">{toText(a.title, 140) || '(untitled)'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* MIL-driven recommended channels — for cleaning /
                    pet sitting / handyman etc this shows Google
                    Business Profile, Nextdoor, Facebook groups,
                    referrals — never Gumroad / Stan Store. */}
                {(v2Summary.channels || []).length > 0 && (
                  <div className="mt-4 border-t border-emerald-500/20 pt-3">
                    <p className="text-[10px] uppercase tracking-wider text-emerald-300/80 mb-2">
                      Where to focus marketing
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="rmb-v2-channels">
                      {v2Summary.channels.slice(0, 4).map((c, i) => (
                        <div
                          key={`${c.name}-${i}`}
                          className="rounded-lg bg-white/5 px-3 py-2"
                          data-testid={`rmb-v2-channel-${i}`}
                        >
                          <p className="text-white text-sm font-medium truncate">{toText(c.name, 60)}</p>
                          {c.why && <p className="text-slate-300 text-xs mt-0.5 line-clamp-2">{toText(c.why, 180)}</p>}
                          {(c.cost || c.timeline) && (
                            <p className="text-slate-400 text-[11px] mt-1">
                              {c.cost && <span>cost: {toText(c.cost, 30)}</span>}
                              {c.cost && c.timeline && <span> · </span>}
                              {c.timeline && <span>{toText(c.timeline, 40)}</span>}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Round 77 — Asset-First Phase 2 — Week 1 priorities.
                    Service-local accounts see local-visibility tasks
                    FIRST (Google Business Profile, Nextdoor, FB groups,
                    referrals, customer follow-up). Each task that has
                    a matching saved asset references it directly with
                    "Use your saved X" copy, instead of "Create X". */}
                {(v2Summary.week1_priorities || []).length > 0 && (
                  <div className="mt-4 border-t border-emerald-500/20 pt-3" data-testid="rmb-v2-week1-priorities">
                    <div className="mb-2 flex items-center gap-2">
                      <p className="text-[10px] uppercase tracking-wider text-emerald-300/80">
                        Week 1 priorities
                      </p>
                      {v2Summary.is_service_local && (
                        <span
                          className="text-[10px] uppercase tracking-wider text-amber-300/90 bg-amber-500/10 border border-amber-400/30 rounded px-1.5 py-0.5"
                          data-testid="rmb-v2-service-local-badge"
                        >
                          local service · customer acquisition first
                        </span>
                      )}
                    </div>
                    <ol className="space-y-2">
                      {v2Summary.week1_priorities.slice(0, 8).map((p, i) => (
                        <li
                          key={`p-${p.order}-${i}`}
                          className="flex items-start gap-2 rounded-lg bg-white/5 px-3 py-2"
                          data-testid={`rmb-v2-priority-${p.order}`}
                        >
                          <span className="text-emerald-300 font-bold text-sm shrink-0 w-5">
                            {p.order}.
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              {p.channel && (
                                <span className="text-[10px] uppercase tracking-wider text-slate-400">
                                  {toText(p.channel, 40)}
                                </span>
                              )}
                              {p.uses_asset && (
                                <span
                                  className="text-[10px] uppercase tracking-wider text-emerald-300 bg-emerald-500/10 border border-emerald-400/30 rounded px-1.5 py-0.5"
                                  data-testid={`rmb-v2-priority-${p.order}-uses-asset`}
                                >
                                  uses saved asset
                                </span>
                              )}
                            </div>
                            <p className="text-white text-sm mt-0.5">
                              {toText(p.task, 280)}
                            </p>
                            {p.why && (
                              <p className="text-slate-400 text-[11px] mt-0.5">
                                {toText(p.why, 200)}
                              </p>
                            )}
                          </div>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </div>
            )}

            {/* Memory banner only when memory is actually populated. We read
                the memory_* fields from the status endpoint to avoid a
                contradiction where the banner says "using saved business"
                but the body says "Business Plan Required". */}
            {(status?.memory_ready || status?.memory_business_name) && (
              <div className="mb-4">
                <BusinessMemoryBanner />
              </div>
            )}
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <SpinnerGap size={40} className="text-purple-500 animate-spin mb-4" />
                <p className="text-slate-400">Loading your business run status...</p>
              </div>
            ) : (!status?.can_start && !status?.started && !status?.memory_ready) ? (
              /* Business Plan Required — ONLY when memory is also missing.
                 When memory_ready is true we bypass this gate entirely,
                 because business_memory IS the business plan. */
              <div className="text-center py-12" data-testid="rmb-memory-missing-gate">
                <Warning size={48} className="text-amber-500 mx-auto mb-4" />
                <h3 className="text-white text-lg font-medium mb-2">
                  Let's finish setting up your business
                </h3>
                <p className="text-slate-400 mb-4 max-w-md mx-auto">
                  {status?.memory_missing_fields?.length
                    ? <>We still need your <span className="text-amber-200">{status.memory_missing_fields.join(', ').replace(/_/g, ' ')}</span> before Run My Business can create on-niche content.</>
                    : <>Complete Business Essentials or the Business Builder so Run My Business can create on-niche content.</>
                  }
                </p>
                <button
                  onClick={() => {
                    onClose();
                    // Route the user to the place they can actually fix this
                    if (typeof window !== 'undefined') {
                      window.dispatchEvent(new CustomEvent('paygbot:open-tool', { detail: { tool: 'essentials' } }));
                    }
                  }}
                  className="px-6 py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg"
                  data-testid="rmb-finish-setup-btn"
                >
                  Finish Business Setup
                </button>
              </div>
            ) : (
              <>
                {/* Progress Tracker */}
                <div className="mb-8">
                  <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                    <Lightning size={18} className="text-purple-400" />
                    Week 1 Progress
                  </h3>
                  <div className="flex items-center justify-center py-4">
                    {PROGRESS_STEPS.map((step, index) => (
                      <ProgressStep
                        key={step.key}
                        step={step}
                        status={
                          // Prefer v2 (asset-registry-driven) status so the
                          // tracker reflects ACTUAL saved assets for the
                          // active business, not the legacy run state.
                          v2Summary?.steps?.[step.key]?.status
                          || status?.steps?.[step.key]?.status
                          || 'pending'
                        }
                        isActive={
                          (v2Summary?.steps?.[step.key]?.status
                            || status?.steps?.[step.key]?.status) === 'in_progress'
                        }
                        index={index}
                        totalSteps={PROGRESS_STEPS.length}
                      />
                    ))}
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-slate-400">Overall Progress</span>
                      <span className="text-purple-400 font-medium">{status?.progress_percent || 0}%</span>
                    </div>
                    <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${status?.progress_percent || 0}%` }}
                        className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Tabs */}
                {isComplete && (
                  <div className="flex gap-2 mb-6 border-b border-slate-700 pb-4">
                    {['overview', 'schedule', 'actions'].map(tab => (
                      <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          activeTab === tab 
                            ? 'bg-purple-600 text-white' 
                            : 'text-slate-400 hover:text-white hover:bg-slate-800'
                        }`}
                      >
                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                      </button>
                    ))}
                  </div>
                )}

                {/* Not Started State */}
                {canStart && !generating && (
                  <div className="text-center py-8">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-4">
                      <Play size={40} className="text-white ml-1" weight="fill" />
                    </div>
                    <h3 className="text-white text-lg font-medium mb-2">Ready to Launch Your Business</h3>
                    <p className="text-slate-400 mb-6 max-w-md mx-auto">
                      PayGBot will generate your complete business foundation, 14 Reels, 21 Stories, 
                      and a 7-day posting schedule.
                    </p>
                    
                    <div className="flex flex-wrap justify-center gap-4 mb-6">
                      <div className="px-4 py-2 bg-slate-800 rounded-lg">
                        <p className="text-white font-medium">6 Foundation Assets</p>
                        <p className="text-slate-400 text-xs">Sales page, Lead magnet, etc.</p>
                      </div>
                      <div className="px-4 py-2 bg-slate-800 rounded-lg">
                        <p className="text-white font-medium">14 Reels</p>
                        <p className="text-slate-400 text-xs">2 per day for 7 days</p>
                      </div>
                      <div className="px-4 py-2 bg-slate-800 rounded-lg">
                        <p className="text-white font-medium">21 Stories</p>
                        <p className="text-slate-400 text-xs">3 per day for 7 days</p>
                      </div>
                    </div>
                    
                    <button
                      onClick={handleGenerateAll}
                      data-testid="start-run-btn"
                      className="px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-medium rounded-xl flex items-center gap-2 mx-auto shadow-lg shadow-purple-500/25"
                    >
                      <Rocket size={20} weight="fill" />
                      Launch Week 1
                    </button>
                  </div>
                )}

                {/* Generating State */}
                {generating && (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-4 animate-pulse">
                      <MagicWand size={40} className="text-white animate-bounce" weight="fill" />
                    </div>
                    <h3 className="text-white text-lg font-medium mb-2">Building Your Business...</h3>
                    <p className="text-slate-400 mb-4">
                      Generating foundation assets, content, and schedule. This may take a few minutes.
                    </p>
                    <div className="flex justify-center gap-2">
                      <SpinnerGap size={20} className="text-purple-400 animate-spin" />
                      <span className="text-purple-400">Please wait...</span>
                    </div>
                  </div>
                )}

                {/* Complete State - Overview Tab */}
                {isComplete && activeTab === 'overview' && (
                  <div className="space-y-6">
                    <div className="text-center py-4">
                      <div className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center mx-auto mb-4">
                        <CheckCircle size={32} className="text-white" weight="fill" />
                      </div>
                      <h3 className="text-white text-lg font-medium">Your First Week is Fully Prepared!</h3>
                      <p className="text-slate-400 mt-2">
                        {schedule?.total_posts || 35} posts ready • {schedule?.reels_count || 14} Reels • {schedule?.stories_count || 21} Stories
                      </p>
                    </div>
                    
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 bg-slate-800/50 rounded-xl text-center">
                        <Package size={24} className="text-purple-400 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-white">6</p>
                        <p className="text-slate-400 text-xs">Foundation Assets</p>
                      </div>
                      <div className="p-4 bg-slate-800/50 rounded-xl text-center">
                        <VideoCamera size={24} className="text-pink-400 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-white">{schedule?.reels_count || 14}</p>
                        <p className="text-slate-400 text-xs">Reels Created</p>
                      </div>
                      <div className="p-4 bg-slate-800/50 rounded-xl text-center">
                        <ImageIcon size={24} className="text-orange-400 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-white">{schedule?.stories_count || 21}</p>
                        <p className="text-slate-400 text-xs">Stories Created</p>
                      </div>
                      <div className="p-4 bg-slate-800/50 rounded-xl text-center">
                        <Calendar size={24} className="text-green-400 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-white">7</p>
                        <p className="text-slate-400 text-xs">Days Planned</p>
                      </div>
                    </div>
                    
                    {/* Metricool CTA */}
                    <div className="p-6 bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 rounded-xl">
                      {/* Push Result Banner */}
                      {pushResult && (
                        <div className={`mb-4 p-3 rounded-lg ${pushResult.failed > 0 ? 'bg-amber-500/20 border border-amber-500/30' : 'bg-green-500/20 border border-green-500/30'}`}>
                          <div className="flex items-center gap-2">
                            {pushResult.failed === 0 ? (
                              <CheckCircle size={20} className="text-green-400" weight="fill" />
                            ) : (
                              <Warning size={20} className="text-amber-400" weight="fill" />
                            )}
                            <span className="text-white font-medium">
                              {pushResult.sent}/{pushResult.total} posts sent to Metricool
                            </span>
                          </div>
                          {pushResult.failed > 0 && (
                            <p className="text-amber-300 text-sm mt-1">
                              {pushResult.failed} post(s) failed. Check schedule for details.
                            </p>
                          )}
                        </div>
                      )}

                      <div className="flex items-center justify-between flex-wrap gap-4">
                        <div>
                          <h4 className="text-white font-medium flex items-center gap-2">
                            <Calendar size={20} className="text-blue-400" />
                            {metricoolStatus?.connected ? 'Connected to Metricool' : 'Ready for Metricool'}
                          </h4>
                          <p className="text-slate-400 text-sm mt-1">
                            {metricoolStatus?.connected 
                              ? `${metricoolStatus.posts_sent || 0} posts sent • Last sync: ${metricoolStatus.last_sync ? new Date(metricoolStatus.last_sync).toLocaleDateString() : 'Never'}`
                              : 'Connect your account to push posts directly to Metricool planner.'
                            }
                          </p>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          {planTier !== 'starter' && (
                            <button
                              onClick={handleExportSchedule}
                              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-lg flex items-center gap-2"
                            >
                              <Download size={18} />
                              Export JSON
                            </button>
                          )}
                          {metricoolStatus?.connected ? (
                            <button
                              onClick={handlePushToMetricool}
                              disabled={pushingToMetricool || pushResult?.sent === pushResult?.total}
                              data-testid="send-to-metricool-btn"
                              className="px-4 py-2 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white font-medium rounded-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {pushingToMetricool ? (
                                <>
                                  <SpinnerGap size={18} className="animate-spin" />
                                  Sending...
                                </>
                              ) : pushResult?.sent === pushResult?.total ? (
                                <>
                                  <CheckCircle size={18} weight="fill" />
                                  Already Sent
                                </>
                              ) : (
                                <>
                                  <PaperPlaneTilt size={18} weight="fill" />
                                  Send Week 1 to Metricool
                                </>
                              )}
                            </button>
                          ) : (
                            <button
                              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg flex items-center gap-2"
                              onClick={() => window.location.href = '/settings'}
                              data-testid="connect-metricool-from-modal-btn"
                            >
                              <Link size={18} weight="bold" />
                              Connect Metricool
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Complete State - Schedule Tab */}
                {isComplete && activeTab === 'schedule' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-white font-medium">7-Day Posting Schedule</h3>
                      <span className="text-slate-400 text-sm">{schedule?.total_posts || 0} total posts</span>
                    </div>
                    
                    {DAY_THEMES.map((dayTheme) => (
                      <DayCard
                        key={dayTheme.day}
                        day={dayTheme.day}
                        theme={dayTheme.theme}
                        color={dayTheme.color}
                        posts={postsByDay[dayTheme.day] || []}
                        expanded={expandedDays[dayTheme.day]}
                        onToggle={() => toggleDay(dayTheme.day)}
                        isToday={dayTheme.day === 1}
                      />
                    ))}
                  </div>
                )}

                {/* Complete State - Actions Tab */}
                {isComplete && activeTab === 'actions' && (
                  <div className="space-y-4">
                    <h3 className="text-white font-medium mb-4">Daily Actions Checklist</h3>

                    {DAY_THEMES.map((dayTheme) => {
                      const plan = dailyPlans[dayTheme.day];
                      const actions = plan?.actions;
                      return (
                        <div key={dayTheme.day} className="p-4 bg-slate-800/50 rounded-xl">
                          <h4 className="text-white font-medium mb-3">Day {dayTheme.day}: {dayTheme.theme}</h4>

                          {/* Round 34 — render tier-specific actions when
                              the live plan is loaded; fall back to the
                              static checklist while loading or on error. */}
                          {actions && actions.length > 0 ? (
                            <div className="space-y-3">
                              {actions.map((act, i) => (
                                <div
                                  key={i}
                                  data-testid={`rmb-day-${dayTheme.day}-action-${i}`}
                                  className="rounded-lg border border-slate-700/50 bg-slate-900/40 p-3"
                                >
                                  <label className="flex items-center gap-2 text-slate-200 text-sm mb-1">
                                    <input type="checkbox" className="rounded bg-slate-700 border-slate-600" />
                                    <Clock size={14} className="text-slate-500" />
                                    <span className="font-medium">{act.time} — {act.action}</span>
                                  </label>
                                  {(act.where_to_use || act.tier_asset) && (
                                    <TaskExecutionMeta
                                      whereToUse={act.where_to_use}
                                      tierAsset={act.tier_asset}
                                      testIdPrefix={`rmb-day-${dayTheme.day}-meta-${i}`}
                                    />
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <label className="flex items-center gap-2 text-slate-300 text-sm">
                                <input type="checkbox" className="rounded bg-slate-700 border-slate-600" />
                                <Clock size={14} className="text-slate-500" /> 09:00 — Post Reel 1
                              </label>
                              <label className="flex items-center gap-2 text-slate-300 text-sm">
                                <input type="checkbox" className="rounded bg-slate-700 border-slate-600" />
                                <Clock size={14} className="text-slate-500" /> 12:00 — Post Story 1 & 2
                              </label>
                              <label className="flex items-center gap-2 text-slate-300 text-sm">
                                <input type="checkbox" className="rounded bg-slate-700 border-slate-600" />
                                <Clock size={14} className="text-slate-500" /> 18:00 — Post Reel 2
                              </label>
                              <label className="flex items-center gap-2 text-slate-300 text-sm">
                                <input type="checkbox" className="rounded bg-slate-700 border-slate-600" />
                                <Clock size={14} className="text-slate-500" /> 20:00 — Post Story 3
                              </label>
                              <label className="flex items-center gap-2 text-slate-300 text-sm">
                                <input type="checkbox" className="rounded bg-slate-700 border-slate-600" />
                                <Clock size={14} className="text-slate-500" /> 21:00 — Respond to comments
                              </label>
                              <label className="flex items-center gap-2 text-slate-300 text-sm">
                                <input type="checkbox" className="rounded bg-slate-700 border-slate-600" />
                                <Clock size={14} className="text-slate-500" /> 21:30 — Check DMs
                              </label>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* In Progress State */}
                {isInProgress && !generating && (
                  <div className="text-center py-8">
                    <SpinnerGap size={40} className="text-purple-400 animate-spin mx-auto mb-4" />
                    <h3 className="text-white text-lg font-medium">Generation in Progress</h3>
                    <p className="text-slate-400 mt-2">
                      Your business content is being generated. Refresh to check progress.
                    </p>
                    <button
                      onClick={fetchStatus}
                      className="mt-4 px-4 py-2 text-purple-400 hover:text-purple-300 flex items-center gap-2 mx-auto"
                    >
                      <ArrowsClockwise size={18} />
                      Refresh Status
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          {isComplete && (
            <div className="border-t border-slate-700 p-4 bg-slate-800/50 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <button
                onClick={handleReset}
                className="text-slate-400 hover:text-red-400 text-sm flex items-center gap-1"
              >
                <ArrowsClockwise size={16} />
                Reset Week 1
              </button>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <button
                  onClick={() => {
                    if (typeof window !== 'undefined') {
                      window.dispatchEvent(
                        new CustomEvent('paygbot:open-tool', { detail: { tool: 'growth' } })
                      );
                    }
                    onClose();
                  }}
                  data-testid="open-run-my-week-btn"
                  className="px-5 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-semibold rounded-lg transition-all flex items-center justify-center gap-2 active:scale-[0.99]"
                >
                  Run My Week
                </button>
                <button
                  onClick={onClose}
                  className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default RunMyBusinessModal;
